import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import { useRouter } from 'next/router'

import { VacanciesReqBody, VacancyDetailsCard } from '@/interfaces/vacancy'
import initLocalStorageData from '@/lib/factory/local-storage'
import { sendListVacanciesRequest } from '@/services/serverless/api'
import LocalStorageData from '@/interfaces/local-storage'
import debounce from '@/lib/debounce'
import { createVacanciesResObj } from '@/lib/mappers/vacancy'
import { getMatchCriteriaVacancyFilters } from '@/services/otys-json-rpc/api/match-criteria'
import { createSingleMatchCriteriaFilterObj } from '@/lib/mappers/match-criteria'
import { listVacancies } from '@/services/otys-json-rpc/api/vacancy'

export interface ServerSideVacancies {
  data: [] | VacancyDetailsCard[]
  totalCount: number
  totalPages: number
}

export async function getServerSideVacanciesData({
  sid,
  pageNumber,
  limitNumber,
  filters,
  searchQuery,
}: {
  sid: string
  pageNumber: number
  limitNumber: number
  filters: string[]
  searchQuery: string
}) {
  const { vacanciesList, totalCount, totalPages } = await listVacancies({
    session_id: sid,
    pageNumber,
    limitNumber,
    filters,
    searchQuery,
  })

  const vacanciesResObj = createVacanciesResObj(vacanciesList, sid)

  const filtersRes = await getMatchCriteriaVacancyFilters({ session_id: sid })

  const filtersData = filtersRes.result?.map((matchCriterium: any) =>
    createSingleMatchCriteriaFilterObj(matchCriterium.data)
  )

  return {
    filtersData,
    vacanciesData: {
      data: vacanciesResObj,
      totalCount,
      totalPages,
    },
  }
}

const useVacanciesData = ({
  serverSideVacancies,
}: {
  serverSideVacancies: ServerSideVacancies
}) => {
  const router = useRouter()
  const searchParams = new URLSearchParams(router.asPath.split('?')[1])
  const pageParam = parseInt(searchParams.get('page') as string, 10) || 1
  const searchQueryParam = searchParams.get('q') || ''
  const filtersParam = searchParams.get('f')
  const filtersParamArray = filtersParam ? filtersParam.split(',') : []

  const [isFirstRender, setFirstRender] = useState(true)
  const [displayTextkernel, setDisplayTextkernel] = useState<boolean>(false)
  const [isLoading, setLoading] = useState(false)
  const [filters, setFilters] = useState<string[]>(filtersParamArray)
  const [searchQuery, setSearchQuery] = useState<string>(searchQueryParam)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isErrorSnackbarOpen, setErrorSnackbarOpen] = useState<boolean>(false)
  const [vacancies, setVacancies] = useState<[] | VacancyDetailsCard[]>(
    serverSideVacancies.data
  )
  const [totalPages, setTotalPages] = useState<number>(
    serverSideVacancies.totalPages
  )
  const [localStorageData] = useLocalStorage<LocalStorageData>(
    'rvdb',
    initLocalStorageData
  )

  const handleCloseErrorSnackbar = () => setErrorSnackbarOpen(false)

  const handleDisplayTextkernel =
    (value: boolean) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      setDisplayTextkernel(value)
    }

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setSearchQuery(value)

    router.replace(
      {
        query: { ...router.query, q: value },
      },
      undefined,
      { shallow: true }
    )
  }

  const handleCheckBoxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target

    setFilters((prev) => {
      if (filters.includes(name)) {
        const newFilters = [...prev].filter((single) => single !== name)
        const filtersAsString = newFilters.join(',')

        router.push(
          {
            pathname: router.pathname,
            query: { ...router.query, f: filtersAsString },
          },
          undefined,
          { shallow: true }
        )

        return newFilters
      }

      const newFilters = [...prev, name]
      const filtersAsString = newFilters.join(',')

      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, f: filtersAsString },
        },
        undefined,
        { shallow: true }
      )

      return [...prev, name]
    })
  }

  const handlePageChange = (page: number) => (e: React.MouseEvent) => {
    fetchVacancies({ pageNumber: page, searchQuery, filters })

    const query = page === 1 ? null : { ...router.query, page }
    router.push(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true }
    )
  }

  const fetchVacancies = (payload: Partial<VacanciesReqBody>) => {
    setLoading(true)

    sendListVacanciesRequest({
      data: payload,
      idToken: localStorageData.idToken,
    })
      .then((res) => {
        const { totalPages, data } = res
        setVacancies(data)
        setTotalPages(totalPages)
      })
      .catch((err) => {
        setErrorMessage(err.message)
        setErrorSnackbarOpen(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const debouncedFetchVacanciesRef = useRef(
    debounce((value: string, filters: string[]) => {
      const payload = {
        searchQuery: value,
        filters,
        pageNumber: pageParam,
      } as VacanciesReqBody

      fetchVacancies(payload)
    }, 500)
  )

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
    } else {
      debouncedFetchVacanciesRef.current(searchQuery, filters)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filters])

  useEffect(() => {
    if (!router.query.page) {
      // don't update the page on when the page is 1
      // the pageParam will still default to 1, so the correct vacancies should be fetched
      const query =
        pageParam === 1
          ? { ...router.query }
          : { ...router.query, page: pageParam }

      router.push(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageParam])

  return {
    vacancies,
    filters,
    searchQuery,
    totalPages,
    currentPage: pageParam,
    errorMessage,
    isErrorSnackbarOpen,
    isLoading,
    displayTextkernel,
    handleCloseErrorSnackbar,
    handleDisplayTextkernel,
    handleCheckBoxChange,
    handleSearchChange,
    handlePageChange,
  }
}

export default useVacanciesData
